import type { QueryRowType } from "@rocicorp/zero";
import type { queries } from "../../../shared/queries";
import {
  estimateUsageCostUsd,
  type ModelPricing,
} from "../../../shared/aiModelPricing";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";

export type MonitoringAiUsageRow = QueryRowType<
  ReturnType<typeof queries.monitoringAiUsageDaily>
> & {
  workflow?:
    | (NonNullable<
        QueryRowType<
          ReturnType<typeof queries.monitoringAiUsageDaily>
        >["workflow"]
      > & {
        space?: DisplayableSpace | null;
      })
    | null;
};

export type ModelUsageRow = {
  model: string;
  provider: string;
  aiProviderId: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
};

export type AiUsageSummary = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
  modelCount: number;
  workflowCount: number;
};

export function recordTokens(record: MonitoringAiUsageRow) {
  return {
    inputTokens: record.inputTokens ?? 0,
    outputTokens: record.outputTokens ?? 0,
    cacheReadTokens: record.cacheReadTokens ?? 0,
    cacheWriteTokens: record.cacheWriteTokens ?? 0,
  };
}

function recordCost(
  model: string,
  record: MonitoringAiUsageRow,
  pricing?: ReadonlyMap<string, ModelPricing>
): number | null {
  return estimateUsageCostUsd(
    model,
    recordTokens(record),
    pricing,
    record.aiProviderId
  );
}

export function computeAiUsageSummary(
  records: readonly MonitoringAiUsageRow[],
  pricing?: ReadonlyMap<string, ModelPricing>
): AiUsageSummary {
  const models = new Set<string>();
  const workflows = new Set<string>();
  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let requestCount = 0;
  let estimatedCostUsd = 0;
  let hasAnyCost = false;

  for (const record of records) {
    const tokens = recordTokens(record);
    inputTokens += tokens.inputTokens;
    outputTokens += tokens.outputTokens;
    cacheReadTokens += tokens.cacheReadTokens;
    requestCount += record.requestCount ?? 0;
    models.add(record.model);
    if (record.workflowId) workflows.add(record.workflowId);

    const cost = recordCost(record.model, record, pricing);
    if (cost != null) {
      estimatedCostUsd += cost;
      hasAnyCost = true;
    }
  }

  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    cacheReadTokens,
    requestCount,
    estimatedCostUsd: hasAnyCost ? estimatedCostUsd : null,
    modelCount: models.size,
    workflowCount: workflows.size,
  };
}

export function computeModelUsageRows(
  records: readonly MonitoringAiUsageRow[],
  pricing?: ReadonlyMap<string, ModelPricing>
): ModelUsageRow[] {
  const byModel = new Map<string, ModelUsageRow>();

  for (const record of records) {
    const key = `${record.provider}:${record.model}`;
    const tokens = recordTokens(record);
    const existing = byModel.get(key);
    if (existing) {
      existing.inputTokens += tokens.inputTokens;
      existing.outputTokens += tokens.outputTokens;
      existing.totalTokens += tokens.inputTokens + tokens.outputTokens;
      existing.cacheReadTokens += tokens.cacheReadTokens;
      existing.requestCount += record.requestCount ?? 0;
      if (existing.aiProviderId == null && record.aiProviderId != null) {
        existing.aiProviderId = record.aiProviderId;
      }
      const cost = recordCost(record.model, record, pricing);
      if (cost != null) {
        existing.estimatedCostUsd = (existing.estimatedCostUsd ?? 0) + cost;
      }
      continue;
    }

    const cost = recordCost(record.model, record, pricing);
    byModel.set(key, {
      model: record.model,
      provider: record.provider,
      aiProviderId: record.aiProviderId ?? null,
      inputTokens: tokens.inputTokens,
      outputTokens: tokens.outputTokens,
      totalTokens: tokens.inputTokens + tokens.outputTokens,
      cacheReadTokens: tokens.cacheReadTokens,
      requestCount: record.requestCount ?? 0,
      estimatedCostUsd: cost,
    });
  }

  return [...byModel.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || a.model.localeCompare(b.model)
  );
}

export function formatTokenCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 10_000) {
    return `${Math.round(count / 1000)}K`;
  }
  return count.toLocaleString("en-US");
}

export function formatCacheHitRate(
  cacheReadTokens: number,
  totalInputTokens: number
): string {
  if (totalInputTokens === 0) return "—";
  return `${Math.round((cacheReadTokens / totalInputTokens) * 100)}%`;
}

export function formatEstimatedCostUsd(amount: number | null): string {
  if (amount == null) return "—";
  if (amount >= 100)
    return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (amount >= 10)
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
